import React, { useEffect, useMemo, useRef, useState } from 'react';

/**
 * PanoOverlay — rendu interactif d'une analyse de panoramique (format webhook pano-v2)
 * + mode « Corriger » (apprentissage actif : envoi de la vérité terrain au dataset Roboflow via n8n).
 *
 * Props :
 *  - src           : dataURL de l'image analysée (celle envoyée au webhook ; nécessaire pour l'envoi de corrections)
 *  - result        : réponse JSON du webhook (success, image, segmentation, restaurations, geometrie…)
 *  - onCorrected   : (resultCorrige) => void — appelé après envoi réussi d'une correction, avec le résultat
 *                    recalculé à partir de l'état corrigé (même format pano-v2, + corrige_manuellement: true)
 *  - correctionUrl : webhook de correction (défaut : https://n8n.cemedis.app/webhook/xray-correction)
 *  - className
 */

export const CORRECTION_WEBHOOK_URL = 'https://n8n.cemedis.app/webhook/xray-correction';

// Palette par catégorie (une couleur = un sens, identique à la légende)
export const PANO_CATEGORIES = {
  dent:                 { label: 'Dent détectée',        color: '#22C55E' },
  implant:              { label: 'Implant',              color: '#3B82F6' },
  couronne:             { label: 'Couronne',             color: '#8B5CF6' },
  bridge:               { label: 'Bridge',               color: '#A855F7' },
  obturation:           { label: 'Obturation',           color: '#0EA5E9' },
  traitement_canalaire: { label: 'Traitement endo',      color: '#14B8A6' },
  carie:                { label: 'Carie suspectée',      color: '#EF4444' },
  lesion_periapicale:   { label: 'Lésion périapicale',   color: '#F43F5E' },
  dent_incluse:         { label: 'Dent incluse',         color: '#F59E0B' },
  racine_residuelle:    { label: 'Racine résiduelle',    color: '#D97706' },
  edentement:           { label: 'Zone édentée',         color: '#94A3B8' }
};
const RESTAURATION_TYPES = Object.keys(PANO_CATEGORIES).filter(k => k !== 'dent');
const CAT2LISTE = {
  implant: 'implants', couronne: 'couronnes', bridge: 'bridges', obturation: 'obturations',
  traitement_canalaire: 'traitements_canalaires', carie: 'caries', lesion_periapicale: 'lesions_periapicales',
  dent_incluse: 'dents_incluses', racine_residuelle: 'racines_residuelles'
};
export const FDI_ALL = ['11','12','13','14','15','16','17','18','21','22','23','24','25','26','27','28',
  '31','32','33','34','35','36','37','38','41','42','43','44','45','46','47','48'];
const QNOMS = { Q1: 'Q1 (11-18) Sup. droit', Q2: 'Q2 (21-28) Sup. gauche', Q3: 'Q3 (31-38) Inf. gauche', Q4: 'Q4 (41-48) Inf. droit' };

const catColor = cat => (PANO_CATEGORIES[cat] || { color: '#64748B' }).color;
const catLabel = cat => (PANO_CATEGORIES[cat] || { label: cat }).label;
const rnd = v => Math.round(v);
let uid = 0;
const newId = () => `e${Date.now().toString(36)}${(uid++).toString(36)}`;

/** Recalcule un résultat pano-v2 à partir d'un état corrigé (dents + restaurations). */
export function appliquerCorrections(result, teeth, rests) {
  const segmentation = {};
  FDI_ALL.forEach(f => {
    segmentation[f] = {
      present: false, confidence: 0, bbox: null, polygon: null, classe_modele: null, corrige: false,
      implant: false, couronne: false, bridge: false, obturation: false, traitement_canalaire: false,
      carie: false, lesion_periapicale: false, dent_incluse: false, racine_residuelle: false, restaurations: []
    };
  });
  teeth.forEach(t => {
    segmentation[t.fdi] = {
      ...segmentation[t.fdi], present: true, confidence: t.confidence || 1, bbox: t.bbox, polygon: t.polygon || null,
      classe_modele: t.classe_modele || null, corrige: !!t.corrige, manuel: t.source === 'manuel'
    };
  });
  const restaurations = rests.map(r => ({
    type: r.type, classe: r.classe || r.type, confidence: r.confidence || 1, bbox: r.bbox, dent: r.dent || null,
    rattachement: r.dent ? 'correction' : null, source: r.source || 'correction'
  }));
  restaurations.forEach(r => {
    if (!r.dent || !segmentation[r.dent]) return;
    const s = segmentation[r.dent];
    s.restaurations.push({ ...r });
    if (r.type in s) s[r.type] = true;
  });
  const listesCat = Object.fromEntries(Object.values(CAT2LISTE).map(k => [k, []]));
  FDI_ALL.forEach(f => Object.keys(CAT2LISTE).forEach(cat => { if (segmentation[f][cat]) listesCat[CAT2LISTE[cat]].push(f); }));
  const presentes = FDI_ALL.filter(f => segmentation[f].present);
  const manquantes = FDI_ALL.filter(f => !segmentation[f].present);
  const quadrants = {};
  ['1', '2', '3', '4'].forEach(q => {
    const dents = presentes.filter(f => f[0] === q);
    quadrants['Q' + q] = { nom: QNOMS['Q' + q], total: 8, presentes: dents.length, manquantes: 8 - dents.length, absentes: 8 - dents.length, implants: listesCat.implants.filter(f => f[0] === q).length, dents };
  });
  const resume = {
    ...(result.resume || {}),
    total_dents_detectees: presentes.length, total_dents_manquantes: manquantes.length,
    dents_presentes: presentes.length, dents_absentes: manquantes.length,
    taux_presence: Math.round(presentes.length / 32 * 100) + '%',
    total_detections: teeth.length,
    ...Object.fromEntries(Object.values(CAT2LISTE).map(k => [k, listesCat[k].length]))
  };
  return {
    ...result,
    corrige_manuellement: true,
    alerte: { niveau: 'info', message: 'Résultat corrigé manuellement et envoyé au jeu d\'apprentissage.' },
    segmentation, restaurations, quadrants, resume,
    listes: { ...(result.listes || {}), dents_presentes: presentes, dents_manquantes: manquantes, absentes: manquantes, ...listesCat, restaurations_non_rattachees: restaurations.filter(r => !r.dent) },
    detections: teeth.map(t => ({ class: t.fdi, confidence: t.confidence || 1 })).concat(restaurations.map(r => ({ class: r.classe, confidence: r.confidence })))
  };
}

export default function PanoOverlay({ src, result, onCorrected, correctionUrl = CORRECTION_WEBHOOK_URL, className = '' }) {
  const W = result?.image?.width || 0;
  const H = result?.image?.height || 0;

  // ----- Affichage -----
  const [hidden, setHidden] = useState({});
  const [showLabels, setShowLabels] = useState(true);
  const [showGuides, setShowGuides] = useState(false);
  const [hover, setHover] = useState(null);

  // ----- Correction -----
  const [edit, setEdit] = useState(false);
  const [teethEdit, setTeethEdit] = useState([]);
  const [restEdit, setRestEdit] = useState([]);
  const [selected, setSelected] = useState(null);         // { kind: 'dent' | 'rest', id }
  const [split, setSplit] = useState('train');
  const [commentaire, setCommentaire] = useState('');
  const [envoi, setEnvoi] = useState({ etat: 'idle', message: '' }); // idle | sending | ok | error
  const svgRef = useRef(null);
  const dragRef = useRef(null);

  const toggle = key => setHidden(h => ({ ...h, [key]: !h[key] }));

  // Données issues du modèle
  const teethModel = useMemo(() => Object.entries(result?.segmentation || {})
    .filter(([, d]) => d && d.present && d.bbox)
    .map(([fdi, d]) => ({ id: 'd' + fdi, fdi, bbox: d.bbox, polygon: d.polygon || null, confidence: d.confidence, corrige: !!d.corrige, classe_modele: d.classe_modele, source: 'modele' })), [result]);
  const restModel = useMemo(() => (result?.restaurations || []).filter(r => r.bbox)
    .map((r, i) => ({ id: 'r' + i, type: r.type, classe: r.classe, bbox: r.bbox, dent: r.dent || null, confidence: r.confidence, rattachement: r.rattachement, source: r.source })), [result]);

  // Réinitialise l'état d'édition quand le résultat change
  useEffect(() => { setTeethEdit(teethModel); setRestEdit(restModel); setSelected(null); setEnvoi({ etat: 'idle', message: '' }); }, [teethModel, restModel]);

  const teeth = edit ? teethEdit : teethModel;
  const restaurations = edit ? restEdit : restModel;

  const presentCats = useMemo(() => {
    const s = new Set(teeth.length ? ['dent'] : []);
    restaurations.forEach(r => s.add(r.type));
    return Object.keys(PANO_CATEGORIES).filter(k => s.has(k));
  }, [teeth, restaurations]);

  if (!result || !W || !H) {
    return <img src={src} alt="Panoramique" className={`w-full h-auto object-contain bg-black ${className}`} />;
  }

  const unit = Math.max(W, H) / 1000;
  const stroke = 1.6 * unit;
  const font = 13 * unit;
  const rectOf = b => ({ x: b.x - b.width / 2, y: b.y - b.height / 2, w: b.width, h: b.height });
  const largeurDent = result.geometrie?.largeur_dent_mediane || W / 18;

  // ----- Conversion souris -> pixels image (letterboxing xMidYMid meet) -----
  const toImg = e => {
    const r = svgRef.current.getBoundingClientRect();
    const scale = Math.min(r.width / W, r.height / H);
    const ox = (r.width - W * scale) / 2, oy = (r.height - H * scale) / 2;
    return { x: (e.clientX - r.left - ox) / scale, y: (e.clientY - r.top - oy) / scale };
  };
  const startDrag = (e, kind, id) => {
    if (!edit) return;
    e.stopPropagation();
    setSelected({ kind, id });
    dragRef.current = { kind, id, last: toImg(e), moved: false };
  };
  const onMove = e => {
    if (!dragRef.current) return;
    const p = toImg(e);
    const dx = p.x - dragRef.current.last.x, dy = p.y - dragRef.current.last.y;
    dragRef.current.last = p; dragRef.current.moved = true;
    const shift = item => ({ ...item, bbox: { ...item.bbox, x: item.bbox.x + dx, y: item.bbox.y + dy }, polygon: item.polygon ? item.polygon.map(([x, y]) => [x + dx, y + dy]) : item.polygon });
    const { kind, id } = dragRef.current;
    if (kind === 'dent') setTeethEdit(list => list.map(t => t.id === id ? shift(t) : t));
    else setRestEdit(list => list.map(r => r.id === id ? shift(r) : r));
  };
  const endDrag = () => { dragRef.current = null; };

  // ----- Opérations d'édition -----
  const selDent = selected?.kind === 'dent' ? teethEdit.find(t => t.id === selected.id) : null;
  const selRest = selected?.kind === 'rest' ? restEdit.find(r => r.id === selected.id) : null;
  const updateSel = patch => {
    if (selDent) setTeethEdit(l => l.map(t => t.id === selDent.id ? { ...t, ...patch } : t));
    if (selRest) setRestEdit(l => l.map(r => r.id === selRest.id ? { ...r, ...patch } : r));
  };
  const resize = (dw, dh) => {
    const it = selDent || selRest; if (!it) return;
    const bbox = { ...it.bbox, width: Math.max(8, it.bbox.width * (1 + dw)), height: Math.max(8, it.bbox.height * (1 + dh)) };
    // un polygone redimensionné devient une boîte (le masque n'est plus fiable)
    updateSel({ bbox, polygon: null });
  };
  const deleteSel = () => {
    if (selDent) setTeethEdit(l => l.filter(t => t.id !== selDent.id));
    if (selRest) setRestEdit(l => l.filter(r => r.id !== selRest.id));
    setSelected(null);
  };
  const occupied = new Set(teethEdit.map(t => t.fdi));
  const addTooth = () => {
    const fdi = FDI_ALL.find(f => !occupied.has(f)) || '11';
    const y = result.geometrie?.separation_arcades_y || H / 2;
    const item = { id: newId(), fdi, bbox: { x: W / 2, y: fdi[0] === '1' || fdi[0] === '2' ? y - largeurDent : y + largeurDent, width: largeurDent, height: largeurDent * 2.2 }, polygon: null, confidence: 1, corrige: false, classe_modele: null, source: 'manuel' };
    setTeethEdit(l => [...l, item]); setSelected({ kind: 'dent', id: item.id });
  };
  const addRest = () => {
    const item = { id: newId(), type: 'obturation', classe: 'obturation', bbox: { x: W / 2, y: H / 2, width: largeurDent * 0.8, height: largeurDent * 0.8 }, dent: null, confidence: 1, source: 'manuel' };
    setRestEdit(l => [...l, item]); setSelected({ kind: 'rest', id: item.id });
  };
  const resetEdits = () => { setTeethEdit(teethModel); setRestEdit(restModel); setSelected(null); setEnvoi({ etat: 'idle', message: '' }); };

  const envoyerCorrection = async () => {
    if (!src || !src.startsWith('data:')) { setEnvoi({ etat: 'error', message: "Image source indisponible en base64 : impossible d'envoyer la correction." }); return; }
    setEnvoi({ etat: 'sending', message: '' });
    const payload = {
      img_b64: src.split(',')[1],
      largeur: W, hauteur: H, split, commentaire: commentaire || undefined,
      dents: teethEdit.map(t => ({ fdi: t.fdi, bbox: { x: rnd(t.bbox.x), y: rnd(t.bbox.y), width: rnd(t.bbox.width), height: rnd(t.bbox.height) }, polygon: t.polygon ? t.polygon.map(([x, y]) => [rnd(x), rnd(y)]) : null })),
      restaurations: restEdit.map(r => ({ type: r.type, bbox: { x: rnd(r.bbox.x), y: rnd(r.bbox.y), width: rnd(r.bbox.width), height: rnd(r.bbox.height) }, dent: r.dent || null })),
      resume_modele: result.resume
    };
    try {
      const resp = await fetch(correctionUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data.ok === false) throw new Error(data.error || data.message || `Erreur ${resp.status}`);
      const env = data.envoyes || {};
      setEnvoi({ etat: 'ok', message: `Correction envoyée (${split}) : ${env.dents ?? teethEdit.length} dents, ${env.protheses ?? 0} prothèses, ${env.restaurations ?? 0} restaurations.` });
      if (onCorrected) onCorrected(appliquerCorrections(result, teethEdit, restEdit));
    } catch (err) {
      setEnvoi({ etat: 'error', message: err.message === 'Failed to fetch' ? "Impossible de contacter le serveur de correction (réseau/CORS)." : err.message });
    }
  };

  const onEnter = (lines, bbox) => { if (!edit) setHover({ x: bbox.x, y: bbox.y - bbox.height / 2, lines }); };
  const isSel = (kind, id) => edit && selected && selected.kind === kind && selected.id === id;

  return (
    <div className={`relative ${className}`}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className={`w-full h-auto block bg-black select-none ${edit ? 'cursor-crosshair' : ''}`}
        style={{ maxHeight: 460 }}
        onMouseLeave={() => { setHover(null); endDrag(); }}
        onMouseMove={onMove}
        onMouseUp={endDrag}
        onClick={() => { if (edit && !(dragRef.current && dragRef.current.moved)) setSelected(null); }}
      >
        <image href={src} x="0" y="0" width={W} height={H} preserveAspectRatio="none" />

        {showGuides && result.geometrie && (
          <g stroke="#FACC15" strokeDasharray={`${6 * unit} ${6 * unit}`} strokeWidth={stroke} opacity="0.8">
            {result.geometrie.ligne_mediane_x != null && <line x1={result.geometrie.ligne_mediane_x} y1="0" x2={result.geometrie.ligne_mediane_x} y2={H} />}
            {result.geometrie.separation_arcades_y != null && <line x1="0" y1={result.geometrie.separation_arcades_y} x2={W} y2={result.geometrie.separation_arcades_y} />}
          </g>
        )}

        {/* Dents */}
        {!hidden.dent && teeth.map(t => {
          const color = isSel('dent', t.id) ? '#FACC15' : catColor('dent');
          const r = rectOf(t.bbox);
          const lines = [
            `Dent ${t.fdi}` + (t.corrige ? ` (modèle : ${t.classe_modele})` : '') + (t.source === 'manuel' ? ' (ajoutée)' : ''),
            `Confiance ${Math.round((t.confidence || 0) * 100)} %`,
            ...(result.segmentation?.[t.fdi]?.restaurations || []).map(x => `${catLabel(x.type)} ${Math.round((x.confidence || 0) * 100)} %`)
          ];
          const dash = t.corrige || t.source === 'manuel' ? `${5 * unit} ${3 * unit}` : undefined;
          return (
            <g key={t.id} onMouseEnter={() => onEnter(lines, t.bbox)} onMouseDown={e => startDrag(e, 'dent', t.id)} onClick={e => edit && e.stopPropagation()} style={{ cursor: edit ? 'move' : 'pointer' }}>
              {t.polygon && t.polygon.length > 2 ? (
                <polygon points={t.polygon.map(p => p.join(',')).join(' ')} fill={color} fillOpacity="0.22" stroke={color} strokeWidth={isSel('dent', t.id) ? stroke * 2 : stroke} strokeDasharray={dash} />
              ) : (
                <rect x={r.x} y={r.y} width={r.w} height={r.h} rx={3 * unit} fill={color} fillOpacity="0.15" stroke={color} strokeWidth={isSel('dent', t.id) ? stroke * 2 : stroke} strokeDasharray={dash} />
              )}
              {showLabels && (
                <g>
                  <rect x={t.bbox.x - 14 * unit} y={t.bbox.y - 9 * unit} width={28 * unit} height={18 * unit} rx={4 * unit} fill="#0F172A" fillOpacity="0.75" />
                  <text x={t.bbox.x} y={t.bbox.y + 4.5 * unit} textAnchor="middle" fontSize={font} fontFamily="ui-sans-serif, system-ui, sans-serif" fontWeight="600" fill="#FFFFFF">{t.fdi}</text>
                </g>
              )}
            </g>
          );
        })}

        {/* Restaurations / anomalies */}
        {restaurations.map(x => {
          if (hidden[x.type]) return null;
          const color = isSel('rest', x.id) ? '#FACC15' : catColor(x.type);
          const r = rectOf(x.bbox);
          const estime = x.rattachement === 'position_estimee';
          const lines = [
            catLabel(x.type) + (x.dent ? ` — dent ${x.dent}` : ''),
            `Confiance ${Math.round((x.confidence || 0) * 100)} %`,
            estime ? 'Position FDI estimée (aucune dent détectée)' : null,
            x.source ? `Modèle : ${x.source}` : null
          ].filter(Boolean);
          const label = `${catLabel(x.type)}${x.dent ? ` ${x.dent}` : ''}`;
          return (
            <g key={x.id} onMouseEnter={() => onEnter(lines, x.bbox)} onMouseDown={e => startDrag(e, 'rest', x.id)} onClick={e => edit && e.stopPropagation()} style={{ cursor: edit ? 'move' : 'pointer' }}>
              <rect x={r.x} y={r.y} width={r.w} height={r.h} rx={3 * unit} fill={color} fillOpacity="0.12" stroke={color} strokeWidth={isSel('rest', x.id) ? stroke * 2.2 : stroke * 1.3} strokeDasharray={estime || x.source === 'manuel' ? `${4 * unit} ${3 * unit}` : undefined} />
              {showLabels && (
                <g>
                  <rect x={r.x} y={r.y - 17 * unit} width={Math.max(r.w, (label.length * 7 + 8) * unit)} height={16 * unit} rx={3 * unit} fill={color} fillOpacity="0.92" />
                  <text x={r.x + 4 * unit} y={r.y - 5 * unit} fontSize={font * 0.85} fontFamily="ui-sans-serif, system-ui, sans-serif" fontWeight="600" fill="#FFFFFF">{label}</text>
                </g>
              )}
            </g>
          );
        })}

        {/* Info-bulle */}
        {hover && !edit && (() => {
          const lw = Math.max(...hover.lines.map(l => l.length)) * 7.2 * unit + 16 * unit;
          const lh = (hover.lines.length * 16 + 10) * unit;
          const tx = Math.min(Math.max(hover.x - lw / 2, 4 * unit), W - lw - 4 * unit);
          const ty = hover.y - lh - 8 * unit > 0 ? hover.y - lh - 8 * unit : hover.y + 8 * unit;
          return (
            <g pointerEvents="none">
              <rect x={tx} y={ty} width={lw} height={lh} rx={5 * unit} fill="#0F172A" fillOpacity="0.92" />
              {hover.lines.map((l, i) => (
                <text key={i} x={tx + 8 * unit} y={ty + (16 * (i + 1) - 2) * unit} fontSize={font * 0.9} fontFamily="ui-sans-serif, system-ui, sans-serif" fill="#F8FAFC" fontWeight={i === 0 ? '700' : '400'}>{l}</text>
              ))}
            </g>
          );
        })()}
      </svg>

      {/* Légende + commandes */}
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-slate-900 text-slate-100 text-xs">
        {presentCats.map(k => (
          <button key={k} type="button" onClick={() => toggle(k)} title={hidden[k] ? 'Afficher' : 'Masquer'}
            className={`flex items-center gap-1.5 px-2 py-1 rounded-full border transition-colors ${hidden[k] ? 'opacity-40 border-slate-600' : 'border-transparent bg-slate-800'}`}>
            <span className="inline-block w-3 h-3 rounded-sm" style={{ background: PANO_CATEGORIES[k].color }} />
            {PANO_CATEGORIES[k].label}
          </button>
        ))}
        <span className="flex-1" />
        <label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={showLabels} onChange={e => setShowLabels(e.target.checked)} /> Numéros</label>
        <label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={showGuides} onChange={e => setShowGuides(e.target.checked)} /> Repères</label>
        <button type="button" onClick={() => { setEdit(v => !v); setSelected(null); }}
          className={`px-2.5 py-1 rounded-full font-medium transition-colors ${edit ? 'bg-amber-400 text-slate-900' : 'bg-violet-600 text-white hover:bg-violet-500'}`}>
          ✏️ {edit ? 'Quitter la correction' : 'Corriger'}
        </button>
      </div>

      {/* Panneau de correction */}
      {edit && (
        <div className="bg-slate-800 text-slate-100 text-xs px-3 py-2 space-y-2 border-t border-slate-700">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-slate-400">Cliquer un élément pour le modifier, glisser pour le déplacer.</span>
            <span className="flex-1" />
            <button type="button" onClick={addTooth} className="px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-500">+ Dent</button>
            <button type="button" onClick={addRest} className="px-2 py-1 rounded bg-sky-600 hover:bg-sky-500">+ Détection</button>
            <button type="button" onClick={resetEdits} className="px-2 py-1 rounded bg-slate-700 hover:bg-slate-600">Réinitialiser</button>
          </div>

          {(selDent || selRest) && (
            <div className="flex flex-wrap items-center gap-2 bg-slate-900 rounded-lg px-2 py-1.5">
              {selDent && (
                <>
                  <span className="font-semibold text-amber-300">Dent</span>
                  <select value={selDent.fdi} onChange={e => updateSel({ fdi: e.target.value, corrige: true })} className="bg-slate-800 border border-slate-600 rounded px-1 py-0.5 text-slate-100">
                    {FDI_ALL.map(f => <option key={f} value={f} disabled={f !== selDent.fdi && occupied.has(f)}>{f}{f !== selDent.fdi && occupied.has(f) ? ' (occupé)' : ''}</option>)}
                  </select>
                </>
              )}
              {selRest && (
                <>
                  <span className="font-semibold text-amber-300">Détection</span>
                  <select value={selRest.type} onChange={e => updateSel({ type: e.target.value, classe: e.target.value })} className="bg-slate-800 border border-slate-600 rounded px-1 py-0.5 text-slate-100">
                    {RESTAURATION_TYPES.map(t => <option key={t} value={t}>{catLabel(t)}</option>)}
                  </select>
                  <span className="text-slate-400">dent</span>
                  <select value={selRest.dent || ''} onChange={e => updateSel({ dent: e.target.value || null })} className="bg-slate-800 border border-slate-600 rounded px-1 py-0.5 text-slate-100">
                    <option value="">—</option>
                    {FDI_ALL.map(f => <option key={f} value={f}>{f}{occupied.has(f) ? '' : ' (absente)'}</option>)}
                  </select>
                </>
              )}
              <span className="text-slate-400 ml-2">Largeur</span>
              <button type="button" onClick={() => resize(-0.08, 0)} className="px-1.5 rounded bg-slate-700">−</button>
              <button type="button" onClick={() => resize(0.08, 0)} className="px-1.5 rounded bg-slate-700">+</button>
              <span className="text-slate-400">Hauteur</span>
              <button type="button" onClick={() => resize(0, -0.08)} className="px-1.5 rounded bg-slate-700">−</button>
              <button type="button" onClick={() => resize(0, 0.08)} className="px-1.5 rounded bg-slate-700">+</button>
              <span className="flex-1" />
              <button type="button" onClick={deleteSel} className="px-2 py-1 rounded bg-rose-600 hover:bg-rose-500">Supprimer</button>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <input type="text" value={commentaire} onChange={e => setCommentaire(e.target.value)} placeholder="Commentaire (optionnel)" className="flex-1 min-w-[160px] bg-slate-900 border border-slate-600 rounded px-2 py-1 text-slate-100" />
            <label className="flex items-center gap-1 cursor-pointer" title="Les images du jeu de validation servent à mesurer le modèle, pas à l'entraîner">
              <input type="checkbox" checked={split === 'valid'} onChange={e => setSplit(e.target.checked ? 'valid' : 'train')} /> Jeu de validation
            </label>
            <button type="button" onClick={envoyerCorrection} disabled={envoi.etat === 'sending'}
              className="px-3 py-1 rounded bg-violet-600 hover:bg-violet-500 disabled:opacity-50 font-medium">
              {envoi.etat === 'sending' ? 'Envoi…' : 'Envoyer la correction'}
            </button>
          </div>
          {envoi.message && (
            <div className={`rounded px-2 py-1 ${envoi.etat === 'ok' ? 'bg-emerald-900/60 text-emerald-200' : 'bg-rose-900/60 text-rose-200'}`}>{envoi.message}</div>
          )}
        </div>
      )}
    </div>
  );
}
